import { log } from "utils/log";

const l = log.miniMap.tiled;

/** Subfolder tiled minimap files live under, inside the SDK's OPFS scene directory (matches `offlineStore.ts`). */
const MINIMAP_DIR_NAME = "minimap";

/** The slice of `OfflineScene` this module needs. */
export type MinimapPruneScene = {
    readonly dir: {
        getJournalEntries(): Promise<IterableIterator<{ name: string; size: number }>>;
        deleteFiles(names: Iterable<string>): Promise<void>;
    };
    reportOfflineFilesRemoved(filenames: string[]): void;
};

/** The `{fid}` a journaled `minimap/{fid}/...` path belongs to, or `undefined` for anything else. */
function fileInstanceIdFromMinimapPath(name: string): string | undefined {
    const segments = name.split("/");
    return segments.length >= 2 && segments[0] === MINIMAP_DIR_NAME ? segments[1] : undefined;
}

function throwIfAborted(abortSignal: AbortSignal | undefined): void {
    if (abortSignal?.aborted) {
        throw (abortSignal.reason as Error | undefined) ?? new Error("Aborted");
    }
}

/**
 * Remove every `minimap/{fid}/...` tree whose `fid` is no longer in
 * `currentFileInstanceIds` -- a sheet that got re-mapped (a re-import mints a
 * new `platformFileInstanceId` rather than reusing the old one, so the old
 * tile set is now orphaned) or removed from the scene outright (BM-13752).
 *
 * Nothing in the SDK does this on its own: eviction and `dir.deleteFiles()`
 * only ever remove the files they're told to, never anything based on
 * whether a `{fid}` is still relevant, and a fully synced scene isn't
 * managed by the eviction loop at all (`OfflineScene.reportOfflineFileAccess`
 * JSDoc, since BM-13747). Without this, a re-mapped or removed sheet's tiles
 * sit on disk indefinitely, displacing geometry cache below the shared
 * incremental cap and outright unbounded on a fully synced scene.
 *
 * @param currentFileInstanceIds The scene's current drawing set, or
 * `undefined` when the caller couldn't determine it (offline, or the
 * drawing-list query failed). `undefined` is treated as "don't know", not
 * "none of these exist anymore" -- pruning nothing rather than risking every
 * `{fid}` looking stale because of a transient API error.
 * @remarks
 * Best-effort and abort-aware: a delete failure is left to the caller to
 * decide whether/how to report (never thrown from here past the abort
 * check), and an aborted `abortSignal` (checked before reading the journal
 * and again before deleting, since enumerating a large journal takes real
 * time) propagates so a user-initiated pause is not mistaken for "pruned
 * nothing, nothing was stale".
 */
export async function pruneStaleMinimapFiles({
    scene,
    currentFileInstanceIds,
    abortSignal,
}: {
    scene: MinimapPruneScene;
    currentFileInstanceIds: readonly string[] | undefined;
    abortSignal?: AbortSignal;
}): Promise<void> {
    if (currentFileInstanceIds === undefined) {
        return;
    }
    throwIfAborted(abortSignal);

    const currentSet = new Set(currentFileInstanceIds);
    const stalePathsByFid = new Map<string, string[]>();
    for (const entry of await scene.dir.getJournalEntries()) {
        const fid = fileInstanceIdFromMinimapPath(entry.name);
        if (!fid || currentSet.has(fid)) {
            continue;
        }
        const paths = stalePathsByFid.get(fid);
        if (paths) {
            paths.push(entry.name);
        } else {
            stalePathsByFid.set(fid, [entry.name]);
        }
    }

    if (stalePathsByFid.size === 0) {
        return;
    }
    throwIfAborted(abortSignal);

    const stalePaths = [...stalePathsByFid.values()].flat();
    l.debug("Pruning stale minimap tile sets:", [...stalePathsByFid.keys()]);
    await scene.dir.deleteFiles(stalePaths);
    scene.reportOfflineFilesRemoved(stalePaths);
}
